import React, { useRef, useEffect, useCallback, ReactNode } from "react";
import { createPortal } from "react-dom";

type PopoverProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  position?: "top" | "right" | "bottom" | "left";
  className?: string;
  triggerRef: React.RefObject<HTMLElement>;
};

export default function Popover({
  open,
  onClose,
  children,
  position = "bottom",
  className = "",
  triggerRef,
}: PopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  // ESC close
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  // animate
  useEffect(() => {
    if (!open) return;
    const pop = popoverRef.current!;
    pop.classList.remove("animate-popIn");
    void pop.offsetWidth;
    pop.classList.add("animate-popIn");
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const trigger = triggerRef.current;
    const pop = popoverRef.current;
    if (!trigger || !pop) return;

    const updatePos = () => {
      const rect = trigger.getBoundingClientRect();
      const popRect = pop.getBoundingClientRect();
      const GAP = 6;

      let finalPosition = position;

      // viewport boundaries
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = window.innerWidth - rect.right;
      const spaceLeft = rect.left;

      // auto flip rules
      if (position === "bottom" && spaceBelow < popRect.height) {
        finalPosition = "top";
      }
      if (position === "top" && spaceAbove < popRect.height) {
        finalPosition = "bottom";
      }
      if (position === "right" && spaceRight < popRect.width) {
        finalPosition = "left";
      }
      if (position === "left" && spaceLeft < popRect.width) {
        finalPosition = "right";
      }

      pop.style.position = "fixed";

      switch (finalPosition) {
        case "bottom":
          pop.style.top = rect.bottom + GAP + "px";
          pop.style.left = rect.right - pop.offsetWidth + "px";
          break;
        case "top":
          pop.style.top = rect.top - pop.offsetHeight - GAP + "px";
          pop.style.left = rect.right - pop.offsetWidth + "px";
          break;
        case "right":
          pop.style.top = rect.top + "px";
          pop.style.left = rect.right + GAP + "px";
          break;
        case "left":
          pop.style.top = rect.top + "px";
          pop.style.left = rect.left - pop.offsetWidth - GAP + "px";
          break;
      }
    };

    updatePos();

    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);

    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, position]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    },
    [onClose, triggerRef]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, handleClickOutside]);

  if (!open) return null;

  return createPortal(
    <div ref={popoverRef} className={`absolute z-10 ${className}`}>
      <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-xl w-full">
        {children}
      </div>
    </div>,
    document.getElementById("popovers")!
  );
}
